import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useState } from 'react';
import { email as validateEmailFormat } from '@/utils/validation';

type ValidationResult = {
    emailAddress: string;
    emailErrorText: string;
    isBidderEmail: boolean;
    isValidEmail: boolean;
    setEmailAddress: (value: string) => void;
    validateEmail: (value: string) => boolean;
};

/**
 * A hook for validating email addresses against an expected email.
 * This hook performs two validations:
 * 1. Checks if the email format is valid (e.g., contains @ and proper domain)
 * 2. Verifies that the email matches the bidder's account email
 *
 * @param email - The bidder's account email to validate against
 * @returns {Object} An object containing:
 *   - emailAddress: The current email address value
 *   - isBidderEmail: Whether the current email matches the bidder's account email
 *   - isValidEmail: Whether the current email is in a valid format
 *   - setEmailAddress: Function to update the email address
 *   - validateEmail: Function to validate both email format and matching
 *
 */
export function useValidateBidderEmail(email: string): ValidationResult {
    const { formatMessage } = useIntl();
    const [emailAddress, setEmailAddress] = useState(email);
    const [isValidEmail, setIsValidEmail] = useState(true);
    const [isBidderEmail, setIsBidderEmail] = useState(true);
    let emailErrorText = '';

    const validateEmail = (value: string) => {
        const isValidEmail = validateEmailFormat(value);
        const isBidderEmail = value === email;
        setIsValidEmail(isValidEmail);
        setIsBidderEmail(isBidderEmail);
        return isValidEmail && isBidderEmail;
    };

    if (!isValidEmail) {
        emailErrorText = formatMessage({ id: 'deleteAccountModal.email.invalidEmail' });
    } else if (!isBidderEmail) {
        emailErrorText = formatMessage({ id: 'deleteAccountModal.email.incorrectEmail' });
    }

    return {
        emailAddress,
        emailErrorText,
        isBidderEmail,
        isValidEmail,
        setEmailAddress,
        validateEmail,
    };
}
